import React from "react";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import PriceList from "@components/PriceList/PriceList";
import Link from "next/link";
import { useInView } from "@hooks/useInView";
import styles from "./CompanySolutionsPage.module.scss";

const FEATURES = [
    {
        key: "biz_feat_actions",
        href: "/adgerdir",
        icon: "/img/icons/laufakerfid.png",
    },
    { key: "biz_feat_goals", href: "/markmid", icon: "/img/icons/markmid.png" },
    {
        key: "biz_feat_carbon",
        href: "/kolefnisutreikningur",
        icon: "/img/icons/kolefnisutreikningur.png",
    },
    {
        key: "biz_feat_supply",
        href: "/fyrirtaeki/birgjamat",
        icon: "/img/icons/birgjamat.png",
    },
    {
        key: "biz_feat_school",
        href: "/laufaskolinn",
        icon: "/img/icons/laufaskolinn.png",
    },
    {
        key: "biz_feat_waste",
        href: "/sounargreining",
        icon: "/img/icons/sounargreining.png",
    },
    {
        key: "biz_feat_food",
        href: "/matarsoun",
        icon: "/img/icons/matarsoun.png",
    },
    {
        key: "biz_feat_atomika",
        href: "/fyrirtaeki/atomika",
        icon: "/img/icons/atomika.png",
    },
    {
        key: "biz_feat_policy",
        href: "/fyrirtaeki/umhverfisstefna",
        icon: "/img/icons/umhverfisstefna.png",
    },
    {
        key: "biz_feat_product",
        href: "/vorur",
        icon: "/img/icons/vorugrunnur.png",
    },
];

export default function CompanySolutionsPage() {
    const t = useTranslations("CompanySolutionsPage");
    const [heroRef, heroInView] = useInView();
    const [featuresRef, featuresInView] = useInView();
    const [showcase1Ref, showcase1InView] = useInView();
    const [showcase2Ref, showcase2InView] = useInView();
    const [pricingRef, pricingInView] = useInView();
    const [diagnosticRef, diagnosticInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Hugbúnaðurinn - " + WEBPAGE_TITLE}
                </title>
                <meta name="keywords" content="Hugbúnaðurinn" />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Hugbúnaðurinn`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("header_desc_new")}
                    key="og:description"
                />
                <meta name="description" content={t("header_desc_new")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("biz_badge")}</span>
                        <h1 className={styles.heroTitle}>
                            {t("biz_hero_title")}
                        </h1>
                        <p className={styles.heroSubtitle}>
                            {t("biz_hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>
                            {t("header_desc_new")}
                        </p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <section
                    ref={featuresRef}
                    className={`${styles.features} ${styles.revealTarget} ${featuresInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.featuresTitle}>
                        {t("core_solutions_for_businesses")}
                    </h2>
                    <div className={styles.featuresGrid}>
                        {FEATURES.map((feat) => (
                            <Link
                                key={feat.key}
                                href={feat.href}
                                className={styles.featureCard}
                            >
                                <img
                                    className={styles.featureIcon}
                                    src={feat.icon}
                                    alt=""
                                />
                                <h3 className={styles.featureTitle}>
                                    {t(`${feat.key}_title`)}
                                </h3>
                                <p className={styles.featureDesc}>
                                    {t(`${feat.key}_desc`)}
                                </p>
                            </Link>
                        ))}
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={showcase1Ref}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${showcase1InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("biz_showcase_actions_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("over_100_action_oriented_steps")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("leafs_msg")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>
                                        120+
                                    </span>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_actions")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>3</span>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_categories")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("biz_stat_categories_esg")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardWide}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <circle cx="12" cy="12" r="6" />
                                            <circle cx="12" cy="12" r="2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_goals")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={showcase2Ref}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${showcase2InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("biz_showcase_dashboard_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("biz_showcase_dashboard_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("biz_showcase_dashboard_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>
                                        4+
                                    </span>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_dashboard_views")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("biz_stat_dashboard_views_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_realtime")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("biz_stat_realtime_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardWide}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="20"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line
                                                x1="12"
                                                y1="20"
                                                x2="12"
                                                y2="4"
                                            />
                                            <line
                                                x1="6"
                                                y1="20"
                                                x2="6"
                                                y2="14"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("biz_stat_reports")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("biz_stat_reports_desc")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={pricingRef}
                    className={`${styles.pricing} ${styles.revealTarget} ${pricingInView ? styles.revealed : ""}`}
                >
                    <PriceList />
                </section>

                <section
                    ref={diagnosticRef}
                    className={`${styles.diagnostic} ${styles.revealTarget} ${diagnosticInView ? styles.revealed : ""}`}
                >
                    <div className={styles.diagnosticInner}>
                        <h2 className={styles.diagnosticTitle}>
                            {t("biz_diagnostic_title")}
                        </h2>
                        <p className={styles.diagnosticDesc}>
                            {t("biz_diagnostic_desc")}
                        </p>
                        <Link className={styles.ctaBtn} href="/greining">
                            {t("biz_diagnostic_btn")}
                        </Link>
                    </div>
                </section>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("biz_bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("biz_bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
